/**
 * SD PDP "Will this fit?" mini-widget logic.
 * - Mode A: render vehicle-specific list from server-rendered data attribute
 * - Mode B: inline YMM dropdowns + check fit
 * - Reuses sessionStorage 'sd-fitment-index' from main fitment widget
 */
(function () {
  'use strict';

  function escape(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function parseFitment(el) {
    try {
      return JSON.parse(el.dataset.vehicleFitment || '[]');
    } catch (_) {
      return [];
    }
  }

  // ── Mode A: render vehicle list ──────────────────────────
  function renderModeA(root) {
    const list = root.querySelector('[data-mode-a-list]');
    if (!list) return;
    const records = parseFitment(root);
    if (!records.length) {
      list.innerHTML = '<li style="color:#6B7280;font-size:.875rem;">No vehicle compatibility on file.</li>';
      return;
    }
    list.innerHTML = records.map(r => {
      const yearRange = r.year_start === r.year_end ? r.year_start : (r.year_start + '–' + r.year_end);
      const trim = r.trim ? ` · <span style="color:#4B5563;">${escape(r.trim)}</span>` : '';
      const side = r.side && r.side !== 'either' ? ` · <span style="color:#4B5563;">${escape(r.side)} side</span>` : '';
      return `<li style="display:flex;align-items:flex-start;gap:.5rem;padding:.5rem 0;border-bottom:1px solid #E5E2DC;font-size:.9375rem;color:#0d1b2e;">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e8a020" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M20 6 9 17l-5-5"/></svg>
        <span><strong>${escape(r.make)} ${escape(r.model)}</strong> · ${escape(yearRange)}${trim}${side}</span>
      </li>`;
    }).join('');
  }

  // ── Mode B: inline YMM ───────────────────────────────────
  async function loadIndex() {
    if (window.SDFitmentCache) {
      const cached = window.SDFitmentCache.read();
      if (cached) return cached;
      return await window.SDFitmentCache.load();
    }
    // Fallback if main widget JS not loaded
    const res = await fetch('/pages/fitment-index', { credentials: 'same-origin' });
    return JSON.parse(await res.text());
  }

  async function setupModeB(root) {
    const yearSel = root.querySelector('[data-pdp-ymm="year"]');
    const makeSel = root.querySelector('[data-pdp-ymm="make"]');
    const modelSel = root.querySelector('[data-pdp-ymm="model"]');
    const checkBtn = root.querySelector('[data-pdp-check-fit]');
    const resultEl = root.querySelector('[data-mode-b-result]');
    if (!yearSel || !makeSel || !modelSel || !checkBtn) return;

    // Year list (descending 2026 → 1990)
    const years = Array.from({ length: 2026 - 1990 + 1 }, (_, i) => 2026 - i);
    yearSel.innerHTML = '<option value="">Year</option>' + years.map(y => `<option value="${y}">${y}</option>`).join('');

    // Build options from THIS product's records (not full index — Mode B is product-scoped)
    const recs = parseFitment(root);
    const allMakes = Array.from(new Set(recs.map(r => r.make))).sort();

    function repopulate() {
      const year = yearSel.value;
      const make = makeSel.value;

      // Filter records by year (if set)
      const yMatch = recs.filter(r => !year || (Number(year) >= r.year_start && Number(year) <= r.year_end));
      const makesAvail = Array.from(new Set(yMatch.map(r => r.make))).sort();
      const prevMake = make;
      makeSel.innerHTML = '<option value="">Make</option>' + (year ? makesAvail : allMakes).map(m => `<option value="${m}">${m}</option>`).join('');
      makeSel.value = (year ? makesAvail : allMakes).includes(prevMake) ? prevMake : '';
      makeSel.disabled = !year;

      const models = Array.from(new Set(
        yMatch.filter(r => !makeSel.value || r.make === makeSel.value).map(r => r.model)
      )).sort();
      const prevModel = modelSel.value;
      modelSel.innerHTML = '<option value="">Model</option>' + models.map(m => `<option value="${m}">${m}</option>`).join('');
      modelSel.value = models.includes(prevModel) ? prevModel : '';
      modelSel.disabled = !makeSel.value;

      checkBtn.disabled = !(year && makeSel.value && modelSel.value);
    }
    [yearSel, makeSel, modelSel].forEach(s => s.addEventListener('change', repopulate));
    repopulate();

    checkBtn.addEventListener('click', () => {
      const year = Number(yearSel.value);
      const make = makeSel.value;
      const model = modelSel.value;

      const match = recs.find(r => r.make === make && r.model === model && year >= r.year_start && year <= r.year_end);
      const handle = root.dataset.productHandle || '';
      const title = root.dataset.productTitle || '';
      const ymm = `${year} ${make} ${model}`;

      if (match) {
        const kitNote = match.required_kit_sku
          ? `<p style="margin:.5rem 0 .75rem;font-size:.875rem;color:#4B5563;">Requires bracket kit (SKU ${escape(match.required_kit_sku)}) — sold separately.</p>`
          : (match.notes ? `<p style="margin:.5rem 0 .75rem;font-size:.875rem;color:#4B5563;">${escape(match.notes)}</p>` : '');
        resultEl.innerHTML = `
          <div style="padding:.875rem 1rem;background:#ECFDF5;border:1px solid #A7F3D0;border-radius:6px;">
            <div style="display:flex;align-items:center;gap:.5rem;font-weight:700;color:#065F46;font-size:.9375rem;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              Fits your ${escape(ymm.toUpperCase())}
            </div>
            ${kitNote}
          </div>
        `;
      } else {
        const params = new URLSearchParams();
        params.set('year', year);
        params.set('make', make);
        params.set('model', model);
        if (handle) params.set('product_handle', handle);
        if (title) params.set('product_title', title);
        resultEl.innerHTML = `
          <div style="padding:.875rem 1rem;background:#FEF3F2;border:1px solid #FECACA;border-radius:6px;">
            <div style="display:flex;align-items:center;gap:.5rem;font-weight:700;color:#7F1D1D;font-size:.9375rem;margin-bottom:.5rem;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c25e3b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
              Fitment not confirmed for ${escape(ymm)}
            </div>
            <p style="font-size:.875rem;color:#4B5563;margin:0 0 .75rem;line-height:1.5;">This spotlight may still mount with a custom bracket — talk to our expert.</p>
            <a href="/pages/ask-a-question?${params.toString()}" style="display:inline-flex;align-items:center;gap:.375rem;padding:.5rem 1rem;background:#0d1b2e;color:#fff;font-size:.8125rem;font-weight:700;border-radius:6px;text-decoration:none;">Ask an expert about your vehicle →</a>
          </div>
        `;
      }
    });
  }

  function init(root) {
    const mode = root.dataset.mode;
    if (mode === 'A') renderModeA(root);
    else if (mode === 'B') setupModeB(root);
    // Mode C: static HTML, no JS needed
  }

  function bootstrap() {
    document.querySelectorAll('[data-pdp-fitment]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
